// Per-user 6-month risk trend sparkline / line chart used by
// UserDetailPage. Reads riskScore.userTrend(email, 6).
//
// Postgres mapping: should read from `user_risk_trend` (user_id, day,
// score) once trend history is persisted; today it is synthesised
// client-side from the current score and a deterministic drift.
// Integration hook: `useUserRiskTrendQuery(email)` cache 6h.
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../../components/cards/MainCard';
import { motion } from 'framer-motion';
import { userTrend, bandFor, BENCHMARK } from '../riskScore';
import useFeature from '../useFeature';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };

// Smooth the line through the real data points with a Catmull-Rom spline
// (converted to cubic beziers). The score is a rolling 180-day window, so
// consecutive monthly points overlap by ~5 months and the true path between
// them is gentle; smoothing reflects that continuity without the cost of
// recomputing the score at weekly resolution.
function smoothPath(pts) {
    if (pts.length < 2) return pts.length ? `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}` : '';
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return d;
}

// Compact 6-month sparkline of this user's score, with an industry-average
// reference dashed across. Anchor + action are surfaced in the modal, here
// we provide visual context so the user-detail page satisfies the spec's
// "individual risk score visible on every relevant user-level surface".
export default function UserRiskTrendChart({ email }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const benchmarkOn = useFeature('industryBenchmarking');
    const data = useMemo(() => userTrend(email, 6), [email]);

    const currentScore = data[data.length - 1]?.score ?? 0;
    const earliest = data[0]?.score ?? currentScore;
    const delta = currentScore - earliest;
    const band = bandFor(currentScore);
    const deltaColor = delta > 0 ? '#FF5252' : delta < 0 ? '#66BB6A' : 'text.secondary';

    const showIndustry = benchmarkOn && BENCHMARK.industryAvg != null;
    const compareValues = showIndustry ? [BENCHMARK.industryAvg] : [];
    const min = Math.max(0, Math.min(...data.map((p) => p.score), ...compareValues) - 6);
    const max = Math.min(100, Math.max(...data.map((p) => p.score), ...compareValues) + 6);
    const range = max - min || 1;

    // Measure the chart container so the SVG fills whatever space the card
    // gives it (the card stretches to match its row neighbour). Drawing in
    // real pixel coordinates keeps markers circular and strokes crisp.
    const chartRef = useRef(null);
    const [size, setSize] = useState({ w: 540, h: 104 });
    useEffect(() => {
        const el = chartRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const r = entries[0]?.contentRect;
            if (r && r.width > 0 && r.height > 0) setSize({ w: r.width, h: r.height });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const LABEL_H = 18; // reserved band for the month labels
    const W = Math.max(120, size.w);
    const H = Math.max(60, size.h - LABEL_H);
    const stepX = data.length > 1 ? W / (data.length - 1) : W;
    const points = data.map((p, i) => ({
        x: i * stepX,
        y: H - ((p.score - min) / range) * H,
        score: p.score,
        label: p.label,
    }));
    const path = smoothPath(points);
    const areaPath = `${path} L ${W} ${H} L 0 ${H} Z`;
    const industryY = showIndustry ? H - ((BENCHMARK.industryAvg - min) / range) * H : 0;

    return (
        <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <Box sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'stretch', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 140, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                            Risk score trend
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 1 }}>
                            {showIndustry ? `6 months - industry avg ${BENCHMARK.industryAvg}` : '6 months - score trajectory'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                            <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: band?.color || 'text.primary', lineHeight: 1 }}>
                                {currentScore}
                            </Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: deltaColor }}>
                                {delta > 0 ? '↑' : delta < 0 ? '↓' : '→'} {Math.abs(delta)} pts / 6mo
                            </Typography>
                        </Box>
                    </Box>

                    <Box ref={chartRef} sx={{ flex: 1, minWidth: 240, minHeight: 96, alignSelf: 'stretch' }}>
                        <Box component="svg" viewBox={`0 0 ${W} ${H + LABEL_H}`} preserveAspectRatio="none" sx={{ width: '100%', height: '100%', display: 'block' }}>
                            {/* Industry reference line, only when benchmarking is on */}
                            {showIndustry && (
                                <>
                                    <line
                                        x1={0} x2={W} y1={industryY} y2={industryY}
                                        stroke="#6B7591" strokeWidth="1" strokeDasharray="4 4" opacity={0.6}
                                    />
                                    <text x={W} y={industryY - 4} textAnchor="end" fontSize="9" fontFamily="Inter,sans-serif" fill={theme.palette.text.primary}>
                                        industry {BENCHMARK.industryAvg}
                                    </text>
                                </>
                            )}
                            {/* Area + line */}
                            <path d={areaPath} fill={band?.color || '#FF5252'} opacity={isDark ? 0.10 : 0.08} />
                            <path d={path} fill="none" stroke={band?.color || '#FF5252'} strokeWidth="2" strokeLinejoin="round" />
                            {/* Markers + month labels */}
                            {points.map((p, i) => (
                                <g key={i}>
                                    <circle cx={p.x} cy={p.y} r="3" fill={band?.color || '#FF5252'} stroke={theme.palette.background.paper} strokeWidth="1.5" />
                                    <text
                                        x={p.x}
                                        y={H + 14}
                                        textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
                                        fontSize="9"
                                        fontFamily="Inter,sans-serif"
                                        fill={theme.palette.text.primary}
                                    >
                                        {p.label}
                                    </text>
                                </g>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </MainCard>
        </motion.div>
    );
}
